/**
 * Marketing Consent Modal
 * Shows once after first login to get marketing email consent (GDPR-compliant)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser, useAuth } from '@clerk/clerk-react';

const CONSENT_STORAGE_KEY = 'sf_marketing_consent_shown';

interface MarketingConsentModalProps {
  onComplete?: () => void;
}

export const MarketingConsentModal: React.FC<MarketingConsentModalProps> = ({ onComplete }) => {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(true);

  useEffect(() => {
    // Only show modal if:
    // 1. User is loaded and signed in
    // 2. Modal hasn't been shown before (localStorage OR backend)
    // 3. User email exists
    const checkAndShowModal = async () => {
      if (!isLoaded || !user?.primaryEmailAddress?.emailAddress) return;
      
      // First check localStorage (fast)
      const hasShownLocally = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (hasShownLocally) return;
      
      // Then check backend (handles cross-browser)
      try {
        const token = await getToken();
        const response = await fetch('/api/email/preferences', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const prefs = await response.json();
          // If user has already set preferences, don't show modal
          if (prefs.has_set_preferences) {
            localStorage.setItem(CONSENT_STORAGE_KEY, 'true');
            return;
          }
        }
      } catch (error) {
        console.error('Failed to check preferences:', error);
      }
      
      // Small delay to let the page load
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    };
    
    checkAndShowModal();
  }, [isLoaded, user, getToken]);

  const handleSubmit = async () => {
    if (!user?.id || !user?.primaryEmailAddress?.emailAddress) return;
    
    setIsSubmitting(true);
    
    try {
      const token = await getToken();
      
      const response = await fetch('/api/email/preferences', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: user.primaryEmailAddress.emailAddress,
          marketingConsent,
          onboardingEmails: true,
          productUpdates: true,
          promotionalEmails: marketingConsent,
        }),
      });

      if (response.ok) {
        // Mark as shown so it doesn't appear again
        localStorage.setItem(CONSENT_STORAGE_KEY, 'true');
        
        // Also identify user for email tracking
        await fetch('/api/email/identify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            email: user.primaryEmailAddress.emailAddress,
            firstName: user.firstName,
            lastName: user.lastName,
            fullName: user.fullName,
            marketingConsent,
          }),
        });
        
        setIsOpen(false);
        onComplete?.();
      } else {
        console.error('Failed to save preferences:', response.status);
      }
    } catch (error) {
      console.error('Failed to save consent:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    // If they skip, default to opted-in (GDPR legitimate interest)
    // but mark as shown
    localStorage.setItem(CONSENT_STORAGE_KEY, 'true');
    setIsOpen(false);
    onComplete?.();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={handleSkip}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={handleSkip}
                className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {/* Header */}
              <div className="bg-gradient-to-r from-[#e11d48] to-[#be123c] px-6 py-8 text-center">
                <div className="text-4xl mb-3">🎉</div>
                <h2 className="text-2xl font-bold text-white">
                  Welcome to SignatureForward!
                </h2>
                <p className="text-white/80 mt-2 text-sm">
                  You're all set to create amazing signatures
                </p>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <p className="text-gray-600 mb-6">
                  One quick thing before you dive in:
                </p>
                
                {/* Consent Checkbox */}
                <label className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-[#e11d48] focus:ring-[#e11d48] cursor-pointer"
                  />
                  <div>
                    <span className="font-medium text-gray-800">
                      Send me tips to get the most out of SignatureForward
                    </span>
                    <p className="text-sm text-gray-500 mt-1">
                      Occasional emails about features, best practices, and special offers. 
                      Unsubscribe anytime.
                    </p>
                  </div>
                </label>
                
                {/* Buttons */}
                <div className="mt-6 space-y-3">
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full py-3 px-4 bg-[#e11d48] hover:bg-[#be123c] text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        Get Started
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
                
                {/* Footer note */}
                <p className="mt-4 text-xs text-gray-400 text-center">
                  You can change your email preferences anytime in Settings.
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

/**
 * Hook to reset consent modal (for testing)
 */
export const useResetConsentModal = () => {
  return () => {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    window.location.reload();
  };
};

export default MarketingConsentModal;
